/**
 * Table of Contents for the Medical Necessity Document Pane.
 *
 * Two pieces, driven by a `showTable` state owned by the PDF panel:
 *
 * - {@link TableOfContentsButton} — a persistent "Table of Contents" button
 *   (icon + label) in the PDF toolbar. Clicking it swaps the document area to
 *   the table view; clicking again returns to the PDF.
 * - {@link TableOfContentsTable} — the full-panel table of the order's source
 *   documents (Visit Date / Category / Name). Clicking a row jumps the PDF to
 *   that document's first page and returns to the PDF view.
 *
 * The Visit Date prefers the per-document `doc_date` persisted by the RPA
 * pipeline (OncoEMR "Last Modified"); older orders without it fall back to
 * the order-level `visitDate` prop, then "—".
 * @module TableOfContents
 */
import { Tags } from "@risalabs_frontend_org/oasis-ui-kit";
import moment from "moment";
import React, { CSSProperties } from "react";
import { formatDocType } from "../../../components/documentMappingList/documentTypeLabel";
import { DocumentPageMapping } from "../../../data-model/medicalPaOrdersModel";

/**
 * Normalizes a date string (e.g. "4/10/2025") to MM/DD/YYYY.
 *
 * @param value - Raw date string, may be empty or unparseable
 * @returns The formatted date, or "" when absent/invalid
 */
export const formatVisitDate = (value?: string): string => {
  if (!value) return "";
  const parsed = moment(
    value,
    ["M/D/YYYY", "MM/DD/YYYY", "YYYY-MM-DD", moment.ISO_8601],
    true,
  );
  return parsed.isValid() ? parsed.format("MM/DD/YYYY") : "";
};

/** Table header cell styles. */
const HEADER_CELL_STYLE: CSSProperties = {
  padding: "10px 16px",
  textAlign: "left",
  verticalAlign: "middle",
  fontFamily: "Lato",
  fontSize: "0.75rem",
  fontWeight: 700,
  lineHeight: "1.125rem",
  color: "#0F0F0F",
  background: "#F5F5F5",
  whiteSpace: "nowrap",
};

/** Body cell styles. */
const CELL_STYLE: CSSProperties = {
  height: "3.375rem",
  padding: "0.625rem",
  textAlign: "left",
  verticalAlign: "middle",
  fontFamily: "Lato",
  fontSize: "0.75rem",
  fontWeight: 400,
  lineHeight: "1.125rem",
  color: "#0F0F0F",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  borderBottom: "0.847px solid #E6E6E6",
};

/** Column widths as fractions of the table width. */
const DATE_COL_WIDTH = "22%";
const TYPE_COL_WIDTH = "38%";

/** Name-column cell text: ExtraTiny SemiBold 11 per the design spec. */
const NAME_CELL_TEXT_STYLE: CSSProperties = {
  display: "block",
  fontFamily: "Lato",
  fontSize: "0.6875rem",
  fontWeight: 600,
  lineHeight: "1.125rem",
  color: "#0F0F0F",
  fontKerning: "none",
  fontFeatureSettings: "'ss01' on",
};

/**
 * List / index icon shown on the toggle button.
 *
 * @returns The rendered SVG icon
 */
const ListIcon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M8 6H21M8 12H21M8 18H21M3 6H3.01M3 12H3.01M3 18H3.01" />
  </svg>
);

export interface TableOfContentsButtonProps {
  /** True while the table view replaces the PDF in the document area. */
  showTable: boolean;
  /** Toggles the table view on/off. */
  onToggle: () => void;
  /** Invoked when the table view is opened. */
  onListOpen?: () => void;
}

/**
 * Persistent "Table of Contents" toolbar button.
 *
 * @param props - Table-view state and callbacks
 * @returns The rendered toolbar button
 */
export const TableOfContentsButton: React.FC<TableOfContentsButtonProps> = ({
  showTable,
  onToggle,
  onListOpen,
}) => (
  <button
    type="button"
    onClick={() => {
      if (!showTable) onListOpen?.();
      onToggle();
    }}
    aria-label="Table of Contents"
    aria-pressed={showTable}
    className="flex h-[30px] cursor-pointer items-center gap-1.5 rounded bg-primaryGray-16 px-2 py-0.5 text-[#0F0F0F] transition-colors aria-pressed:bg-primaryGray-15"
  >
    <ListIcon />
    <span className="whitespace-nowrap text-[9px] font-normal leading-4 text-[#0F0F0F] [font-family:Lato] [font-feature-settings:'ss01'_on] [font-kerning:none]">
      Table of Contents
    </span>
  </button>
);

export interface TableOfContentsTableProps {
  /** Source-document page ranges for the merged PDF. */
  mappings: DocumentPageMapping[];
  /** Order-level date used when a mapping has no per-document `doc_date`. */
  visitDate?: string;
  /** Invoked with the 1-based page to scroll the PDF to (row selection). */
  onSelectPage: (page: number) => void;
  /** Invoked when a document row is selected. */
  onReportSelected?: (mapping: DocumentPageMapping) => void;
}

/**
 * Full-panel table of the order's source documents.
 *
 * @param props - Document mappings, order-level date, and selection callbacks
 * @returns The rendered table panel
 */
export const TableOfContentsTable: React.FC<TableOfContentsTableProps> = ({
  mappings,
  visitDate,
  onSelectPage,
  onReportSelected,
}) => {
  const orderedMappings = [...mappings].sort(
    (a, b) => (a.merge_order ?? 0) - (b.merge_order ?? 0),
  );

  const handleSelect = (mapping: DocumentPageMapping) => {
    const startPage = mapping.start_page;
    if (typeof startPage === "number" && startPage > 0) {
      onReportSelected?.(mapping);
      onSelectPage(startPage);
    }
  };

  const dateFor = (mapping: DocumentPageMapping): string =>
    formatVisitDate(mapping.doc_date) || formatVisitDate(visitDate) || "—";

  return (
    <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border border-primaryGray-15 bg-white">
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            <tr>
              <th style={{ ...HEADER_CELL_STYLE, width: DATE_COL_WIDTH }}>
                Visit Date
              </th>
              <th style={{ ...HEADER_CELL_STYLE, width: TYPE_COL_WIDTH }}>
                Category
              </th>
              <th style={HEADER_CELL_STYLE}>Name</th>
            </tr>
          </thead>
          <tbody>
            {orderedMappings.map((mapping, index) => (
              <tr
                key={`${mapping.doc_name ?? "doc"}-${mapping.start_page ?? index}`}
                onClick={() => handleSelect(mapping)}
                className="cursor-pointer hover:bg-primaryGray-16"
              >
                <td style={CELL_STYLE}>{dateFor(mapping)}</td>
                <td style={CELL_STYLE}>
                  <Tags
                    label={formatDocType(mapping.doc_type) || "—"}
                    backgroundColor="#F5F5F5"
                    textColor="#0F0F0F"
                  />
                </td>
                <td style={CELL_STYLE}>
                  <span style={NAME_CELL_TEXT_STYLE}>
                    {mapping.doc_name || "Untitled document"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableOfContentsButton;
